import { CustomListsAutocompleteForSequencerItem } from "../../List/components/CustomListsAutocompleteForSequencer";
import { List } from "../../List/schema/list";

/** The reserved key on `campaignConfig` holding list-level metadata, not a campaign. */
const LIST_NAME_KEY = "name";

/**
 * `campaignConfig` is `z.any()` in the list schema (its shape varies per campaign builder
 * version), so it is narrowed here to the one field this file reads.
 */
type CampaignConfig = Record<string, { name?: string } | undefined>;

/**
 * Turns a list from the lean endpoint into a selection entry, with every campaign
 * included.
 *
 * Every campaign starts active because picking a list has always meant "report on all of
 * it"; the Campaign filter is what narrows it afterwards. Shared by the List filter, the
 * quick filters and the `?listId=` deep link so all three produce the same shape — they
 * used to hold three copies of it, and a divergence there reads as "the campaign filter is
 * empty for lists I picked one way but not the other".
 */
export function toSelectedList(list: List): CustomListsAutocompleteForSequencerItem {
	const config = (list.campaignConfig || {}) as CampaignConfig;

	return {
		id: list.id,
		title: list.name,
		campaigns: Object.keys(config)
			.filter((campaignId) => campaignId !== LIST_NAME_KEY)
			.map((campaignId) => ({
				campaignId,
				// An unnamed campaign still has to be pickable, so fall back to its id.
				campaignName: config[campaignId]?.name ?? campaignId,
				active: true,
			})),
	};
}

/** The campaign a click-through opens, as the Overview and Top Campaigns rows describe it. */
export interface CampaignClickTarget {
	listId: number;
	listName: string;
	campaignId: string;
	campaignName: string;
}

/**
 * A selection scoped to one campaign that still knows the list's others, switched off.
 *
 * Opening a campaign from the Overview or Top Campaigns tab used to select a list holding
 * that campaign alone, so the Campaign filter offered nothing else and read "All" over a
 * report of one campaign in seven (PREC-4215). With the rest present the filter reads
 * "1 of 7" and the report can be widened again. Without the list (not loaded, or the fetch
 * failed) the selection is the campaign alone, which is still the right scope.
 */
export function selectOnlyCampaign(
	target: CampaignClickTarget,
	list: List | undefined
): CustomListsAutocompleteForSequencerItem {
	const clicked = {
		campaignId: target.campaignId,
		campaignName: target.campaignName,
		active: true,
	};
	const others = list
		? (toSelectedList(list)
				.campaigns?.filter((campaign) => campaign.campaignId !== target.campaignId)
				.map((campaign) => ({ ...campaign, active: false })) ?? [])
		: [];

	return {
		id: target.listId,
		title: list?.name ?? target.listName,
		campaigns: [clicked, ...others],
	};
}
